import {
  ChangeConfigurationStatus,
  ConfigurationType,
  ConfigureResponse,
  getComponentModelForChargingStation,
  getConfiguration,
  GetConfigurationCommand,
  GetConfigurationResponseStatus,
  getFullConfiguration,
  setConfiguration as setConfigurationApi,
  SetConfigurationCommand
} from '@infuse/api/cpms/operator';
import { NoOcppCommandResultsError, useOcppCommandMutationUtils } from '@infuse/cpms/charging-station/mutations';
import { UiNotificationType } from '@infuse/ui/domain';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { apiComponentGroupToComponentGroup, mapDomainToComponentDto, mapDomainToVariableDto } from './conversion';
import { AttributeType, Component, ComponentGroup, CreateVariable, Variable } from './model';

export interface ComponentModelFilter {
  evseId?: number;
  connectorId?: number;
  variableName?: string;
}

export const useComponentModelStore = defineStore('component-model', () => {
  // State
  const item = ref<ComponentGroup | undefined>(undefined);
  const unfilteredItem = ref<ComponentGroup | undefined>(undefined);
  const options = ref({
    chargingStationId: undefined as string | undefined,
    filter: {} as ComponentModelFilter
  });
  const loadingVariables = ref<{ name: string; instance?: string }[]>([]);
  const loadingFull = ref(false);
  const loading = computed(() => loadingVariables.value.length > 0 || loadingFull.value);
  const configMayBeOutdated = ref(false);

  // Initialize OCPP utils in setup context
  const ocppUtils = useOcppCommandMutationUtils();

  // Actions
  const findByChargingStationId = async (chargingStationId: string, loadOutDatedFirst?: boolean) => {
    const { waitForOcppResults, apiCorrelationToken, chargingStationCommandResults } = ocppUtils;

    try {
      loadingFull.value = true;
      options.value.chargingStationId = chargingStationId;

      if (loadOutDatedFirst) {
        configMayBeOutdated.value = true;
        // Get a currently stored component model, this may be outdated, but we will do a full refresh in the meantime
        const apiResponse = await getComponentModelForChargingStation({
          chargingStationId: options.value.chargingStationId
        });

        // We display this initially to the user
        item.value = apiComponentGroupToComponentGroup(apiResponse);
        unfilteredItem.value = apiComponentGroupToComponentGroup(apiResponse);
      }

      // Now let's also do a full refresh
      const { apiCorrelationToken: fullConfigurationApiCorrelationToken } = await getFullConfiguration(
        { chargingStationId: options.value.chargingStationId },
        { configurationType: ConfigurationType.FULL_INVENTORY }
      );

      // This takes some time.
      apiCorrelationToken.value = fullConfigurationApiCorrelationToken;

      await waitForOcppResults();

      // since the data we receive from /get-full-configuration and waiting for /ocpp-command-results is of a different
      // type (the domain class ReportData - not a DTO), that also has no structured components, we simply request the stored (and now updated) component model again
      if (chargingStationCommandResults.value !== undefined) {
        const apiResponse = await getComponentModelForChargingStation({
          chargingStationId: options.value.chargingStationId
        });

        item.value = apiComponentGroupToComponentGroup(apiResponse);
        unfilteredItem.value = apiComponentGroupToComponentGroup(apiResponse);
        configMayBeOutdated.value = false;
      }
    } catch (e) {
      if (e instanceof NoOcppCommandResultsError) {
        configMayBeOutdated.value = true;
      }
    } finally {
      loadingFull.value = false;
    }
  };

  const filterVariablesByName = (variableName: string) => {
    const searchTerm = variableName.toLowerCase();
    return (variable: Variable) => variable.name.toLowerCase().includes(searchTerm);
  };

  const filterComponentsByVariables = (components: Component[], variableFilter: (v: Variable) => boolean) => {
    components.forEach((component) => {
      component.variables = component.variables.filter(variableFilter);
    });
    return components.filter((component) => component.variables.length > 0);
  };

  const filterComponentGroup = (group: ComponentGroup, variableFilter: (v: Variable) => boolean) => {
    // Filter direct components
    group.components = filterComponentsByVariables(group.components, variableFilter);

    // Recursively filter nested component groups
    group.componentGroups.forEach((nestedGroup) => {
      filterComponentGroup(nestedGroup, variableFilter);
    });

    // Remove empty nested groups
    group.componentGroups = group.componentGroups.filter(
      (nestedGroup) => nestedGroup.components.length > 0 || nestedGroup.componentGroups.length > 0
    );
  };

  const applyFilter = async (filter: ComponentModelFilter) => {
    const filteredItem = deepCopy(unfilteredItem.value);

    if (!filteredItem) {
      return;
    }

    if (filter.variableName && filter.variableName !== '') {
      const variableFilter = filterVariablesByName(filter.variableName);

      // Filter top-level components
      filteredItem.components = filterComponentsByVariables(filteredItem.components, variableFilter);

      // Filter component groups recursively
      filteredItem.componentGroups.forEach((group) => {
        filterComponentGroup(group, variableFilter);
      });

      // Remove empty top-level groups
      filteredItem.componentGroups = filteredItem.componentGroups.filter(
        (group) => group.components.length > 0 || group.componentGroups.length > 0
      );
    }

    item.value = filteredItem;
  };

  const resetOptions = () => {
    options.value.chargingStationId = undefined;
    options.value.filter = {};
    configMayBeOutdated.value = false;
    item.value = deepCopy(unfilteredItem.value);
  };

  const getVariable = async (chargingStationId: string, variable: Variable, attributeType: AttributeType) => {
    const { waitForOcppResults, apiCorrelationToken, chargingStationCommandResults } = ocppUtils;

    if (!item.value) {
      throw new Error('No component model loaded');
    }

    const result = findComponentAndComponentGroupOfVariable(item.value, variable);
    if (!result) {
      throw new Error('No component found for variable');
    }

    const getVariablesCommand: GetConfigurationCommand = {
      configurationValues: [
        {
          attributeType: attributeType.toString() as AttributeType,
          component: mapDomainToComponentDto(result.componentGroup, result.component),
          variable: mapDomainToVariableDto(variable)
        }
      ]
    };

    loadingVariables.value.push({ name: variable.name, instance: variable.instance });

    try {
      const { apiCorrelationToken: fullConfigurationApiCorrelationToken } = await getConfiguration(
        { chargingStationId: chargingStationId },
        getVariablesCommand
      );

      apiCorrelationToken.value = fullConfigurationApiCorrelationToken;

      await waitForOcppResults({
        statusHandlers: [
          {
            responseStatus: GetConfigurationResponseStatus.REJECTED,
            notificationType: UiNotificationType.ERROR,
            translationKey: 'chargingStation.action.getConfiguration.response.rejected'
          },
          {
            responseStatus: GetConfigurationResponseStatus.UNKNOWN_VARIABLE,
            notificationType: UiNotificationType.ERROR,
            translationKey: 'chargingStation.action.getConfiguration.response.unknownVariable'
          },
          {
            responseStatus: GetConfigurationResponseStatus.UNKOWN_COMPONENT,
            notificationType: UiNotificationType.ERROR,
            translationKey: 'chargingStation.action.getConfiguration.response.unknownComponent'
          },
          {
            responseStatus: GetConfigurationResponseStatus.NOT_SUPPORTED_ATTRIBUTE_TYPE,
            notificationType: UiNotificationType.ERROR,
            translationKey: 'chargingStation.action.getConfiguration.response.notSupportedAttributeType'
          }
        ]
      });

      if (chargingStationCommandResults.value !== undefined) {
        // Update the specific variable in the store with the command result
        const commandResult = chargingStationCommandResults.value.results[0]?.commandResult;
        if (commandResult && commandResult.componentVariables) {
          updateStoreWithCommandResults(commandResult.componentVariables, GetConfigurationResponseStatus.ACCEPTED);
        }
      }
    } finally {
      loadingVariables.value = loadingVariables.value.filter(
        (v) => v.name !== variable.name || v.instance !== variable.instance
      );
    }
  };

  const updateStoreWithCommandResults = (
    componentVariables: Array<{
      status: string;
      attributeValue?: string;
      component: { name: string };
      variable: { name: string };
      attributeType: string;
    }>,
    acceptedStatus: ChangeConfigurationStatus | GetConfigurationResponseStatus
  ) => {
    if (!item.value) {
      return;
    }

    for (const componentVariable of componentVariables) {
      if (componentVariable.status === acceptedStatus && componentVariable.attributeValue) {
        // Update both item and unfilteredItem
        item.value = updateVariableInComponentGroup(
          item.value,
          componentVariable.component.name,
          componentVariable.variable.name,
          componentVariable.attributeType as AttributeType,
          componentVariable.attributeValue
        );

        if (unfilteredItem.value) {
          unfilteredItem.value = updateVariableInComponentGroup(
            unfilteredItem.value,
            componentVariable.component.name,
            componentVariable.variable.name,
            componentVariable.attributeType as AttributeType,
            componentVariable.attributeValue
          );
        }
      }
    }
  };

  const setVariable = async (
    chargingStationId: string,
    variable: Variable,
    attributeType: AttributeType,
    newValue: string,
    options: { waitForResponse?: boolean } = {}
  ): Promise<ConfigureResponse | undefined> => {
    if (!item.value) {
      throw new Error('No component model loaded');
    }

    const result = findComponentAndComponentGroupOfVariable(item.value, variable);
    if (!result) {
      throw new Error('No component found for variable');
    }

    const setVariablesCommand: SetConfigurationCommand = {
      configurationValues: [
        {
          attributeType: attributeType.toString() as AttributeType,
          component: mapDomainToComponentDto(result.componentGroup, result.component),
          variable: mapDomainToVariableDto(variable),
          attributeValue: newValue
        }
      ]
    };

    return setConfiguration(chargingStationId, variable, setVariablesCommand, options);
  };

  const createVariable = async (
    chargingStationId: string,
    variable: CreateVariable,
    attributeType: AttributeType,
    component: Component,
    componentGroup: ComponentGroup,
    newValue: string,
    options: { waitForResponse?: boolean } = {}
  ): Promise<ConfigureResponse | undefined> => {
    const setVariablesCommand: SetConfigurationCommand = {
      configurationValues: [
        {
          attributeType: attributeType.toString() as AttributeType,
          component: mapDomainToComponentDto(componentGroup, component),
          variable: mapDomainToVariableDto(variable),
          attributeValue: newValue
        }
      ]
    };

    return setConfiguration(chargingStationId, variable, setVariablesCommand, options);
  };

  const setConfiguration = async (
    chargingStationId: string,
    variable: CreateVariable,
    setVariablesCommand: SetConfigurationCommand,
    options: { waitForResponse?: boolean } = {}
  ) => {
    const { waitForResponse = true } = options;
    const { waitForOcppResults, apiCorrelationToken, chargingStationCommandResults } = ocppUtils;

    loadingVariables.value.push({ name: variable.name, instance: variable.instance });
    let configurationResponse;

    try {
      configurationResponse = await setConfigurationApi({ chargingStationId: chargingStationId }, setVariablesCommand);

      if (waitForResponse) {
        apiCorrelationToken.value = configurationResponse.apiCorrelationToken;

        await waitForOcppResults({
          statusHandlers: [
            {
              responseStatus: ChangeConfigurationStatus.REJECTED,
              notificationType: UiNotificationType.ERROR,
              translationKey: 'chargingStation.action.setConfiguration.response.rejected'
            },
            {
              responseStatus: ChangeConfigurationStatus.NOT_SUPPORTED,
              notificationType: UiNotificationType.ERROR,
              translationKey: 'chargingStation.action.setConfiguration.response.notSupported'
            },
            {
              responseStatus: ChangeConfigurationStatus.REBOOT_REQUIRED,
              notificationType: UiNotificationType.WARNING,
              translationKey: 'chargingStation.action.setConfiguration.response.reboot'
            }
          ]
        });

        if (chargingStationCommandResults.value !== undefined) {
          // Update the specific variable in the store with the command result
          const commandResult = chargingStationCommandResults.value.results[0]?.commandResult;
          if (commandResult && commandResult.componentVariables) {
            updateStoreWithCommandResults(commandResult.componentVariables, ChangeConfigurationStatus.ACCEPTED);
          }
        }
      }
    } finally {
      loadingVariables.value = loadingVariables.value.filter(
        (v) => v.name !== variable.name || v.instance !== variable.instance
      );
    }
    return configurationResponse;
  };

  function isLoadingVariable(variable: Variable): boolean {
    return loadingVariables.value.some((v) => v.name === variable.name && v.instance === variable.instance);
  }

  return {
    // State
    item: computed(() => item.value),
    unfilteredItem: computed(() => unfilteredItem.value),
    options: computed(() => options.value),
    loading: computed(() => loading.value),
    loadingFull: computed(() => loadingFull.value),
    loadingVariables: computed(() => loadingVariables.value),
    configMayBeOutdated: computed(() => configMayBeOutdated.value),

    // Functions
    isLoadingVariable,

    // Actions
    findByChargingStationId,
    applyFilter,
    resetOptions,
    getVariable,
    setVariable,
    createVariable
  };
});

const findComponentAndComponentGroupOfVariable = (
  componentGroup: ComponentGroup,
  targetVariable: Variable
): { component: Component; componentGroup: ComponentGroup } | null => {
  for (const component of componentGroup.components) {
    for (const variable of component.variables) {
      if (variable === targetVariable) {
        return { component: component, componentGroup: componentGroup };
      }
    }
  }

  return null;
};

const deepCopy = (componentGroup?: ComponentGroup): ComponentGroup | undefined => {
  if (componentGroup) {
    return JSON.parse(JSON.stringify(componentGroup)) as ComponentGroup;
  }
  return undefined;
};

const updateVariableInComponentGroup = (
  componentGroup: ComponentGroup,
  componentName: string,
  variableName: string,
  attributeType: AttributeType,
  attributeValue: string
): ComponentGroup => {
  // Create a deep copy to ensure reactivity
  const updatedGroup = deepCopy(componentGroup);

  // Helper function to update the attribute value
  const updateAttribute = (group: ComponentGroup): boolean => {
    // Check components in this group
    for (const component of group.components) {
      if (component.name === componentName) {
        for (const variable of component.variables) {
          if (variable.name === variableName) {
            // Find the existing attribute and update its value
            const attribute = variable.attributes.find((attr) => attr.type === attributeType);
            if (attribute) {
              attribute.value = attributeValue;
              return true;
            }
            return false;
          }
        }
      }
    }

    // Recursively search in nested component groups
    for (const nestedGroup of group.componentGroups) {
      if (updateAttribute(nestedGroup)) {
        return true;
      }
    }

    return false;
  };

  updateAttribute(updatedGroup);
  return updatedGroup;
};
